import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Button } from 'blaze-ui/Button'
import { Upload5, Trash2, ArrowRefresh2, File, Calendar2, ChevronDown2 } from 'blaze-ui/icons'
import { ArrowRight } from 'blaze-ui/icons/20'
import { Pdf, Markdown, TextFile } from 'blaze-ui/icons/24'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { Form, Formik } from 'formik'
import { useCallback, useState, useRef, useEffect } from 'react'
import { FormikHelpers } from 'formik/dist/types'
import Styles from './CampaignDetails.module.scss'
import { CampaignDetailsProps, FormValues } from './Types'
import * as yup from 'yup'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Input } from 'almanac-ui/TextInputField/Input'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useBrandKitSources } from 'hooks/queries/useBrandKitSources'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { Text } from 'blaze-ui/Text/Text'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { collectResults } from 'utils/query'
import { useFileUpload } from 'components/FirstTimeExperienceModal/steps/BrandProfileDocuments/useFileUpload'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { useGenerateCampaignInputs } from 'hooks/queries/brandPlans/useGenerateCampaignInputs'
import { TextFieldLoadingState } from 'components/Wizards/TextFieldLoadingState/TextFieldLoadingState'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { getLocalTimeZone, today } from '@internationalized/date'
import dayjs from 'dayjs'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useCreateOnboardingCampaign } from 'hooks/queries/adCampaigns/useCreateOnboardingCampaign'
import { reportError } from 'utils/error'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useRootStore } from 'stores/RootStore'
import { StrategyEditDrawer } from './StrategyEditDrawer'
const MAX_FILES = 10

const validationSchema = yup.object().shape({
  growth_goals_input: yup.string(),
  key_dates_input: yup.string(),
})

const getFileIcon = (doc: any) => {
  const mimeType = doc.source?.mime_type

  if (mimeType === 'application/pdf') {
    return <Pdf size={20} color="var(--red-50)" />
  }
  if (mimeType === 'text/markdown') {
    return <Markdown size={20} color="var(--green-50)" />
  }
  if (mimeType === 'text/plain') {
    return <TextFile size={20} color="var(--teal-50)" />
  }
  if (mimeType?.includes('word') || mimeType?.includes('document')) {
    return <File size={20} color="var(--blue-50)" />
  }

  return <File size={20} color="var(--dark-80)" />
}

const getUploadButtonText = (isUploading: boolean, fileCount: number) => {
  if (isUploading) return 'Uploading...'
  if (fileCount >= MAX_FILES) return 'Max 10 files'
  if (fileCount > 0) return 'Upload more'
  return 'Upload your brand strategy docs'
}

const CampaignDetailsStep = (props: CampaignDetailsProps) => {
  const isAdsOnboardingEnabled = useFlipperFlag('ads_onboarding')
  const { onBack, onNext, initialValues, brandPlanId, brandKitId, paidAdsOnboardingParameters, onProvideBackHandler } =
    props
  const { isMobile } = useResponsiveDesign()
  const { drawersStore } = useRootStore()

  const { mutate: generateGoals, isLoading: isGeneratingGoals } = useGenerateCampaignInputs(brandPlanId)
  const { mutate: generateDates, isLoading: isGeneratingDates } = useGenerateCampaignInputs(brandPlanId)
  const { mutate: createOnboardingCampaign } = useCreateOnboardingCampaign({
    onError: (error) => {
      reportError(error, { severity: 'warning' })
    },
  })
  const [isGeneratingBoth, setIsGeneratingBoth] = useState(false)
  const hasInitialGeneratedRef = useRef(false)
  const [startDate, setStartDate] = useState<string>(initialValues?.startDate || dayjs().add(1, 'day').toISOString())

  const { handleUpload, isLoading: isUploading } = useFileUpload(brandKitId)

  useEffect(() => {
    if (onProvideBackHandler && isMobile) {
      onProvideBackHandler(onBack)
    }
  }, [onBack, onProvideBackHandler, isMobile])

  const documentsQuery = useBrandKitSources(
    {
      brandKitId: brandKitId,
      sourceType: ['SourceMaterialsMediaObject'],
    },
    {
      enabled: !!brandKitId,
      refetchOnWindowFocus: false,
    },
  )
  const documents = collectResults(documentsQuery, (data) => data.brand_kit_sources) || []

  const { mutate: deleteSource } = useDeleteBrandKitSource({
    onSuccess: () => {
      documentsQuery.refetch()
    },
  })

  const handleGenerateGoals = useCallback(
    (setFieldValue: (field: string, value: any) => void) => {
      generateGoals(undefined, {
        onSuccess: (data) => {
          setFieldValue('growth_goals_input', data.growth_goals_input)
        },
      })
    },
    [generateGoals],
  )

  const handleGenerateDates = useCallback(
    (setFieldValue: (field: string, value: any) => void) => {
      generateDates(undefined, {
        onSuccess: (data) => {
          setFieldValue('key_dates_input', data.key_dates_input)
        },
      })
    },
    [generateDates],
  )

  const handleGenerateBoth = useCallback(
    (setFieldValue: (field: string, value: any) => void) => {
      setIsGeneratingBoth(true)

      generateGoals(undefined, {
        onSuccess: (data) => {
          setFieldValue('growth_goals_input', data.growth_goals_input)
          setFieldValue('key_dates_input', data.key_dates_input)
          setIsGeneratingBoth(false)
        },
        onError: () => {
          setIsGeneratingBoth(false)
        },
      })
    },
    [generateGoals],
  )

  const handleFilesSelected = useCallback(
    async (fileList: FileList | null, setFieldValue?: (field: string, value: any) => void) => {
      if (!fileList || !brandKitId) return

      const currentCount = documents.length
      const availableSlots = Math.max(0, MAX_FILES - currentCount)

      if (availableSlots === 0) {
        showErrorMessageToast(`You can upload a maximum of ${MAX_FILES} files.`)
        return
      }

      const files = Array.from(fileList).slice(0, availableSlots)

      if (fileList.length > availableSlots) {
        showErrorMessageToast(
          `Only ${availableSlots} file${availableSlots > 1 ? 's' : ''} can be added. Maximum ${MAX_FILES} files total.`,
        )
      }

      await handleUpload(files)
      await documentsQuery.refetch()

      if (setFieldValue) {
        handleGenerateBoth(setFieldValue)
      }
    },
    [brandKitId, handleUpload, documentsQuery, documents.length, handleGenerateBoth],
  )

  const handleDeleteDocument = useCallback(
    (sourceId: number) => {
      if (!brandKitId) return
      deleteSource({ brandKitId, id: sourceId })
    },
    [brandKitId, deleteSource],
  )

  const handleSubmit = useCallback(
    async ({ growth_goals_input, key_dates_input }: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      try {
        const shouldOnboardForAds =
          !!paidAdsOnboardingParameters &&
          !!paidAdsOnboardingParameters?.selectedPlatforms?.length &&
          paidAdsOnboardingParameters?.weeklyAdsBudget !== undefined
        if (isAdsOnboardingEnabled && shouldOnboardForAds) {
          createOnboardingCampaign({ weeklyBudget: paidAdsOnboardingParameters.weeklyAdsBudget ?? 0 })
        }

        onNext({ growth_goals_input, key_dates_input, startDate })
      } catch (err) {
        showErrorMessageToast('Failed to proceed, please try again.')
        setSubmitting(false)
      }
    },
    [onNext, startDate, paidAdsOnboardingParameters, createOnboardingCampaign, isAdsOnboardingEnabled],
  )

  const openStrategyDrawer = useCallback(
    (title: string, value: string, fieldName: string, setFieldValue: (field: string, value: any) => void) => {
      drawersStore.openDrawer(StrategyEditDrawer, {
        title,
        value,
        onSave: (newValue: string) => {
          setFieldValue(fieldName, newValue)
        },
      })
    },
    [drawersStore],
  )

  const renderDocumentsSection = (setFieldValue: (field: string, value: any) => void) => {
    if (!brandKitId) return null
    return (
      <div className={Styles.documentsSection}>
        <div className={Styles.documentsHeading}>
          <div className={Styles.headingRow}>
            <Heading level={3}>Add strategy documents</Heading>
            <span className={Styles.optionalBadge}>
              <Text variant="secondary" color="var(--dark-80)">
                Optional
              </Text>
            </span>
          </div>
          <Text variant="secondary" color="var(--dark-40)">
            Upload brand guidelines or marketing plans to enhance Blaze's understanding of your goals.
          </Text>
        </div>

        {documents.length > 0 && (
          <div className={Styles.documentsList}>
            {documents.map((doc) => (
              <div key={doc.id} className={Styles.documentRow}>
                <div className={Styles.documentInfo}>
                  {getFileIcon(doc)}
                  <Text variant="secondary" color="var(--dark-80)" lineClamp={1}>
                    {doc.name || 'Document'}
                  </Text>
                </div>
                <IconButton
                  icon={Trash2}
                  variant="tertiary"
                  size="lg"
                  onPress={() => handleDeleteDocument(doc.id)}
                  aria-label="Delete file"
                />
              </div>
            ))}
          </div>
        )}

        <div className={Styles.documentButtons}>
          <FileTrigger
            onSelect={(files) => handleFilesSelected(files, setFieldValue)}
            accept=".pdf,.doc,.docx,.txt,.md"
            allowsMultiple={true}
            aria-label="Upload Files"
          >
            <Button
              variant="secondary"
              size={isMobile ? 'lg' : 'xl'}
              frontIcon={Upload5}
              fullWidth={isMobile}
              isDisabled={isUploading || documentsQuery.isLoading || documents.length >= MAX_FILES}
            >
              {getUploadButtonText(isUploading, documents.length)}
            </Button>
          </FileTrigger>
        </div>
      </div>
    )
  }

  const renderTextField = (
    fieldName: 'growth_goals_input' | 'key_dates_input',
    heading: string,
    helperText: string,
    placeholder: string,
    isGenerating: boolean,
    onRegenerate: () => void,
    values: FormValues,
    handleChange: any,
    handleBlur: any,
    errors: any,
    touched: any,
    setFieldValue: (field: string, value: any) => void,
  ) => {
    const value = values[fieldName]

    if (isMobile) {
      return (
        <div className={Styles.field}>
          <div className={Styles.fieldHeader}>
            <Heading level={3}>{heading}</Heading>
            <Text variant="secondary" color="var(--dark-40)">
              {helperText}
            </Text>
          </div>
          {isGenerating ? (
            <TextFieldLoadingState lines={4} />
          ) : (
            <div
              className={Styles.readOnlyField}
              onClick={() => openStrategyDrawer(heading, value, fieldName, setFieldValue)}
            >
              <Text variant="secondary" color={value ? 'var(--dark-80)' : 'var(--dark-40)'}>
                {value || placeholder}
              </Text>
            </div>
          )}
          <Button
            variant="tertiary"
            size="sm"
            frontIcon={ArrowRefresh2}
            onPress={onRegenerate}
            isDisabled={isGenerating}
          >
            Regenerate
          </Button>
        </div>
      )
    }

    return (
      <div className={Styles.field}>
        <div className={Styles.fieldHeader}>
          <Heading level={3}>{heading}</Heading>
          <div className={Styles.helperRow}>
            <Text variant="secondary" color="var(--dark-40)">
              {helperText}
            </Text>
            <Button
              variant="tertiary"
              size="sm"
              frontIcon={ArrowRefresh2}
              onPress={onRegenerate}
              isDisabled={isGenerating}
            >
              Regenerate
            </Button>
          </div>
        </div>
        {isGenerating ? (
          <TextFieldLoadingState lines={4} />
        ) : (
          <Input
            id={fieldName}
            name={fieldName}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            isTextarea
            isValid={!errors[fieldName]}
            errorMessage={touched[fieldName] ? errors[fieldName] : undefined}
          />
        )}
      </div>
    )
  }

  const description = isMobile
    ? "We'll use this to build your long-term content plan aligned with your business goals."
    : 'Answer these questions to help us build a 12-month content plan aligned with your business goals and key moments.'

  const content = (
    <MultiStepStepLayout
      title="Review your content strategy"
      description={description}
      scrollContainerClassName={Styles.scrollContainer}
    >
      <Formik
        initialValues={{
          growth_goals_input: initialValues?.growth_goals_input || '',
          key_dates_input: initialValues?.key_dates_input || '',
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ values, handleChange, handleBlur, errors, touched, submitForm, isSubmitting, setFieldValue }) => {
          // Trigger initial generation once when component mounts with empty fields
          if (
            !hasInitialGeneratedRef.current &&
            !values.growth_goals_input &&
            !values.key_dates_input &&
            !isGeneratingGoals &&
            !isGeneratingDates &&
            brandPlanId
          ) {
            hasInitialGeneratedRef.current = true
            handleGenerateBoth(setFieldValue)
          }

          return (
            <StickyFooterProvider
              footer={
                <StickyFooter>
                  <FooterButtons>
                    {!isMobile && (
                      <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back" isDisabled={isSubmitting}>
                        Back
                      </Button>
                    )}
                    <Button
                      size="lg"
                      onPress={submitForm}
                      aria-label="Generate my campaigns"
                      isDisabled={isSubmitting}
                      fullWidth={isMobile}
                      endIcon={isMobile ? ArrowRight : undefined}
                    >
                      Generate My Campaigns
                    </Button>
                  </FooterButtons>
                </StickyFooter>
              }
            >
              <Form className={Styles.form}>
                <div className={Styles.content}>
                  {!isMobile && renderDocumentsSection(setFieldValue)}

                  {renderTextField(
                    'growth_goals_input',
                    'What are your key growth goals for the next 12 months?',
                    'AI-generated based on your business. Click to edit or regenerate.',
                    'e.g. Double revenue; Launch a new product...',
                    isGeneratingGoals || isGeneratingBoth,
                    () => handleGenerateGoals(setFieldValue),
                    values,
                    handleChange,
                    handleBlur,
                    errors,
                    touched,
                    setFieldValue,
                  )}

                  {renderTextField(
                    'key_dates_input',
                    'What are key dates or milestones for your business?',
                    'AI-generated. Click to edit or regenerate.',
                    'e.g. September 12th is our product launch, Black Friday is huge for us, tax filing day is April 15th...',
                    isGeneratingDates || isGeneratingBoth,
                    () => handleGenerateDates(setFieldValue),
                    values,
                    handleChange,
                    handleBlur,
                    errors,
                    touched,
                    setFieldValue,
                  )}

                  {isMobile && renderDocumentsSection(setFieldValue)}

                  <div className={Styles.datePickerRow}>
                    <Heading level={3}>When should your first campaign start?</Heading>
                    <DateTimePicker
                      disableDatesBefore={today(getLocalTimeZone()).add({ days: 1 }).toString()}
                      dateTime={dayjs(startDate)}
                      setDateTime={(date) => {
                        if (!date) return
                        setStartDate(date.format())
                      }}
                      showTimeControls={false}
                    >
                      <Button frontIcon={Calendar2} size="xl" variant="secondary" endIcon={ChevronDown2}>
                        {dayjs(startDate).format('dddd, MMM D')}
                      </Button>
                    </DateTimePicker>
                  </div>
                </div>
              </Form>
            </StickyFooterProvider>
          )
        }}
      </Formik>
    </MultiStepStepLayout>
  )

  if (isMobile) {
    return <div className={Styles.mobileWrapper}>{content}</div>
  }

  return content
}

export { CampaignDetailsStep as CampaignDetails }
